import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus, FileText, Trash2, Search, Eye, X, Pencil, MessageCircle, Share, Instagram, ChevronUp, ChevronDown, Mail, AlertTriangle, Copy, LayoutGrid, List, Clock, CheckCircle, XCircle, ArrowUpDown, BookOpen, PhoneCall, CalendarX, Repeat, History, RefreshCw } from 'lucide-react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import { OfferPDF } from './OfferPDF';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import { roomLineTotal, offerDiscount, discountLabel, formatMoneyTR } from '../../utils/pricing';
import { useSeason } from '../../context/SeasonContext';
import { offerInSeason, offerSeason, isValidSeason, ALL_SEASONS } from '../../utils/season';
import { API_URL, jsonAuthHeaders, readJsonOrThrow } from '../../utils/api';
import { matchesSearch, phoneKey, emailKey, instagramKey } from '../../utils/text';
import {
  dateKey, formatDateTR, formatDateRangeTR, formatStay, offerMoneyText, offerRoomsText, waPhone,
  buildOfferMessage, buildOfferEmail, whatsappUrl, mailtoUrl, instagramUrl,
} from '../../utils/offerShare';
import { FOLLOW_UP, offerFollowUp, followUpList, followUpAgeText, checkInSoonText, staleOffers } from '../../utils/followUp';
import { customerOffers, customerSummary, customerSummaryText, repeatCustomerIndex } from '../../utils/customer';
import { fetchMessageSettings, logOfferShare, fetchOfferPdfIds, confirmPdfBeforeShare } from '../../utils/shareSettings';

// Teklif listesinde sayfa başına gösterilecek kayıt sayısı
const PAGE_SIZE = 30;

const STATUSES = ['Onay Bekliyor', 'Onaylandı', 'Onaylanmadı'];
// URL süzgeçleri (Gösterge Paneli ile ortak): /offers?filter=takip · /offers?filter=gecmis
const FOLLOW_FILTERS = {
  takip: 'Takip edilecekler',
  gecmis: 'Tarihi geçmiş bekleyenler',
};
// Müşteri geçmişinde gösterilecek en fazla teklif
const HISTORY_LIMIT = 5;

// Kopyalamada taşınan alanlar (beyaz liste). API'den gelen nesne olduğu gibi yayılmaz: id, createdAt/By, updatedAt/By,
// validityDate, deletedAt/By ve ileride eklenecek bilinmeyen kolonlar kopyaya geçmez. checkIn/checkOut ayrıca
// 'YYYY-MM-DD'ye çevrilir (API'nin ISO 'Z' metni DATE kolonuna bir gün kaymış yazılıyordu).
const DUPLICATE_FIELDS = ['phone', 'email', 'instagram', 'notes', 'currency', 'nights', 'totalPrice', 'source', 'room', 'rooms'];
// İndirim kolonları OfferForm'daki gibi yalnızca kaynak teklifte indirim varsa gönderilir
const DISCOUNT_FIELDS = ['discountName', 'discountType', 'discountValue', 'discountAmount'];

// Müşteri geçmişi (utils/customer) ile aynı ölçüt: dolgu değerler ('-', 'yok') gerçek e-posta / Instagram sayılmaz
const realEmail = (value) => {
  const k = emailKey(value);
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(k) ? k : '';
};
const realInstagram = (value) => {
  const k = instagramKey(value);
  return /^[a-z0-9._]{3,30}$/.test(k) && /[a-z]/.test(k) ? k : '';
};
// "Listede göster" araması: geçmişi eşleştiren gerçek anahtar — telefon (son 10 hane), e-posta, Instagram, en son ad
const customerSearchKey = (offer) => (
  phoneKey(offer?.phone)
  || realEmail(offer?.email)
  || realInstagram(offer?.instagram)
  || String(offer?.customerName || '').trim()
);

const statusPillClass = (status) => (
  status === 'Onaylandı' ? 'bg-green-100 text-green-800'
    : status === 'Onaylanmadı' ? 'bg-red-100 text-red-800'
    : 'bg-yellow-100 text-yellow-800'
);

// Ayar okuma: başarısız istekte boş nesne (paylaşım/PDF varsayılanlarla çalışmaya devam eder)
const readSetting = async (category) => {
  try {
    const res = await fetch(`${API_URL}/settings/${category}`);
    if (!res.ok) return {};
    const body = await res.json();
    return body && typeof body === 'object' ? body : {};
  } catch {
    return {};
  }
};

// 'Onay Bekliyor' teklifin bekleme rozetleri: süre (3+ gün sarı, 7+ gün kırmızı), girişe kalan gün, giriş geçti
function FollowUpBadges({ f, className = '' }) {
  if (!f) return null;
  const age = followUpAgeText(f);
  const soon = checkInSoonText(f);
  if (!age && !soon && !f.stale) return null;
  const base = 'inline-flex items-center gap-1 text-[11px] leading-4 px-1.5 py-0.5 rounded-full whitespace-nowrap';
  const ageCls = f.level === 'late'
    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 font-semibold'
    : f.level === 'warn'
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 font-semibold'
      : 'bg-gray-100 text-gray-500 dark:bg-gray-700/60 dark:text-gray-400';
  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`}>
      {age && (
        <span className={`${base} ${ageCls}`} title="Teklifin verildiği günden bu yana cevap bekleniyor">
          <Clock size={11} /> {age}
        </span>
      )}
      {soon && (
        <span className={`${base} bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 font-semibold`} title="Giriş tarihi yaklaşıyor">
          <PhoneCall size={11} /> {soon}
        </span>
      )}
      {f.stale && (
        <span className={`${base} bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300`} title="Giriş tarihi geçtiği hâlde teklif hâlâ 'Onay Bekliyor'">
          <CalendarX size={11} /> Giriş tarihi geçti
        </span>
      )}
    </div>
  );
}

// Aynı telefon/e-postayla birden fazla teklif varsa küçük "Tekrar müşteri" rozeti
function RepeatBadge({ count }) {
  if (!count || count < 2) return null;
  return (
    <span
      title={`Bu müşteriye ait ${count} teklif var`}
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
    >
      <Repeat size={10} /> Tekrar müşteri
    </span>
  );
}

// Sunucuda PDF'i olmayan teklif: gönderilen link müşteriye teklif yerine "bağlantınız güncellendi" sayfası açar
function NoPdfBadge({ show }) {
  if (!show) return null;
  return (
    <span
      title="Sunucuda bu teklifin PDF'i yok. Teklifi açıp kaydedince PDF oluşur."
      className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
    >
      PDF yok
    </span>
  );
}

export default function OfferList() {
  const { currentUser } = useAuth();
  const { data, errors, fetchData, refreshData, loading, incrementReads, incrementWrites } = useData();
  const { selectedSeason, activeSeason, setSelectedSeason } = useSeason();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOffer, setSelectedOffer] = useState(null);
  // ?q= arama kutusunu doldurur (Gösterge Paneli / müşteri geçmişi)
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') || '');
  // ?creator=<e-posta> oluşturan süzgecini doldurur (Gösterge Paneli "yalnızca benim")
  const [creatorFilter, setCreatorFilter] = useState(() => searchParams.get('creator') || '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [createdAtStart, setCreatedAtStart] = useState('');
  const [createdAtEnd, setCreatedAtEnd] = useState('');
  const [settings, setSettings] = useState({});
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'kanban'
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, offerId: null });
  const [deleting, setDeleting] = useState(false);
  const [priceListModal, setPriceListModal] = useState({ isOpen: false, offer: null });
  // Mesaj şablonları (Ayarlar > Mesaj Şablonları; boş alan = varsayılan metin)
  const [messages, setMessages] = useState({});
  // Kısa bilgi (ör. "Teklif silindi…") — birkaç saniye sonra kendiliğinden kapanır
  const [notice, setNotice] = useState('');
  // Sunucuda PDF'i olan teklif numaraları (Set) — null: bilinmiyor (rozet ve uyarı gösterilmez)
  const [pdfIds, setPdfIds] = useState(null);
  const pdfRequestRef = useRef(0);
  const duplicatingRef = useRef(false);

  // Yetki Kontrolleri
  const canWrite = currentUser?.permissions?.write;
  const canDelete = currentUser?.permissions?.delete;
  const canViewFinancials = !!currentUser?.permissions?.view_financials;

  // Durum ve takip süzgeçleri URL'de tutulur (?status=…, ?filter=takip|gecmis) → Gösterge Paneli linkleriyle uyumlu
  const urlStatus = searchParams.get('status');
  const statusFilter = STATUSES.includes(urlStatus) ? urlStatus : '';
  const urlFilter = searchParams.get('filter');
  const followFilter = Object.prototype.hasOwnProperty.call(FOLLOW_FILTERS, urlFilter || '') ? urlFilter : '';
  const urlQuery = searchParams.get('q');
  const urlCreator = searchParams.get('creator');
  // Teklifler okunamadı (DataContext errors) — eski liste (varsa) ekranda kalır
  const offersError = errors?.offers ? String(errors.offers) : '';

  const updateParams = (changes) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      return next;
    }, { replace: true });
  };

  // Durum ve takip süzgeci tek seçimdir: biri seçilince öteki kalkar
  const selectStatus = (status) => updateParams({ status: status || null, filter: null });
  const selectFollowFilter = (filter) => updateParams({ filter: filter || null, status: null });

  // URL'deki ?q= değişirse (ör. "Listede göster") arama kutusu güncellenir
  useEffect(() => {
    if (urlQuery !== null) setSearchTerm(urlQuery);
  }, [urlQuery]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    // Elle yazılan arama URL'deki eski ?q= ile çakışmasın
    if (searchParams.has('q')) updateParams({ q: null });
  };

  // URL'deki ?creator= değişirse oluşturan süzgeci güncellenir
  useEffect(() => {
    if (urlCreator !== null) setCreatorFilter(urlCreator);
  }, [urlCreator]);

  const handleCreatorChange = (value) => {
    setCreatorFilter(value);
    if (searchParams.has('creator')) updateParams({ creator: null });
  };

  // PDF listesi: açılışta ve PDF'i etkileyebilecek işlemlerden sonra (silme, kopyalama, durum) tazelenir.
  // Sıra numarası: geç dönen eski yanıt yenisinin üzerine yazmasın.
  const loadPdfIds = useCallback(async () => {
    const seq = ++pdfRequestRef.current;
    const ids = await fetchOfferPdfIds();
    if (seq === pdfRequestRef.current) setPdfIds(ids);
  }, []);
  useEffect(() => {
    loadPdfIds();
    // Ekrandan çıkılınca bekleyen yanıt yazılmasın
    return () => { pdfRequestRef.current += 1; };
  }, [loadPdfIds]);
  const pdfMissing = (offer) => !!pdfIds && !!offer?.id && !pdfIds.has(Number(offer.id));

  const retryOffers = () => {
    refreshData('offers');
    loadPdfIds();
  };

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), 6000);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    const fetchSettings = async () => {
      const [logosData, bankData, messageData] = await Promise.all([
        readSetting('logos'),
        readSetting('bank'),
        fetchMessageSettings(),
      ]);
      incrementReads(3); // 3 döküman okundu
      setSettings({ ...logosData, bank: bankData });
      setMessages(messageData);
    };

    fetchData('offers');
    fetchData('roomTypes');
    fetchSettings();
  }, [fetchData, incrementReads]);

  const allOffers = useMemo(() => (Array.isArray(data.offers) ? data.offers : []), [data.offers]);
  // Üstteki sezon seçicisi: "Tüm Sezonlar" dışında yalnızca o sezonun teklifleri görünür
  const offers = useMemo(() => allOffers.filter(o => offerInSeason(o, selectedSeason)), [allOffers, selectedSeason]);
  const rooms = data.roomTypes || [];

  // Bekleyen tekliflerin takip durumu (id → offerFollowUp) — rozetler için
  const followUpById = useMemo(() => {
    const now = new Date();
    const map = new Map();
    allOffers.forEach((o) => {
      const f = offerFollowUp(o, now);
      if (f) map.set(String(o.id), f);
    });
    return map;
  }, [allOffers]);
  const followUpOf = (offer) => followUpById.get(String(offer?.id)) || null;

  // Oluşturan süzgeci. URL'den gelen e-posta (Gösterge Paneli "yalnızca benim") TAM eşleşir — panelle aynı ölçüt;
  // kutuya elle yazılan metin (URL'deki değer silinir) e-postanın içinde aranır.
  const creatorExact = urlCreator !== null && creatorFilter === urlCreator;
  const creatorOffers = useMemo(() => {
    if (!creatorFilter.trim()) return offers;
    if (creatorExact) {
      const key = emailKey(creatorFilter);
      return offers.filter(o => emailKey(o?.createdBy?.email) === key);
    }
    return offers.filter(o => matchesSearch([o?.createdBy?.email], creatorFilter));
  }, [offers, creatorFilter, creatorExact]);
  const creatorIds = useMemo(() => new Set(creatorOffers.map(o => String(o.id))), [creatorOffers]);

  // Takip edilecekler / tarihi geçmiş bekleyenler (seçili sezon + oluşturan süzgeci içinde → panel sayısıyla aynı)
  const followIds = useMemo(() => new Set(followUpList(creatorOffers).map(x => String(x.offer.id))), [creatorOffers]);
  const staleIds = useMemo(() => new Set(staleOffers(creatorOffers).map(o => String(o.id))), [creatorOffers]);

  // "Tekrar müşteri": sezon süzgecinden ÖNCEKİ tüm teklifler üzerinden sayılır
  const repeatCountOf = useMemo(() => repeatCustomerIndex(allOffers), [allOffers]);

  // Paylaşım mesajları için bağlam (şablonlar, marka ayarları, e-posta şablonu, site adresi)
  const shareCtx = { messages, logos: settings, bank: settings.bank, origin: window.location.origin };

  const findOffer = (id) => allOffers.find(o => String(o.id) === String(id));

  const handleDelete = (id) => {
    setDeleteModal({ isOpen: true, offerId: id });
  };

  // Yumuşak silme: teklif "Silinenler"e taşınır; işlem logunu sunucu yazar
  const executeDelete = async () => {
    if (!deleteModal.offerId || deleting) return;
    const id = deleteModal.offerId;
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/offers/${id}`, {
          method: 'DELETE',
          headers: jsonAuthHeaders(currentUser?.token)
      });
      await readJsonOrThrow(res, 'Teklif silinemedi.');
      incrementWrites(1);
      setDeleteModal({ isOpen: false, offerId: null });
      setSelectedIds(prev => prev.filter(x => String(x) !== String(id)));
      if (selectedOffer && String(selectedOffer.id) === String(id)) setSelectedOffer(null);
      refreshData('offers');
      loadPdfIds();
      setNotice("Teklif silindi. Ayarlar > Silinenler'den geri alınabilir.");
    } catch (error) {
      console.error("Silme hatası:", error);
      alert(`Teklif silinemedi: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const count = selectedIds.length;
    if (!window.confirm(`${count} adet teklifi silmek istediğinize emin misiniz?\n\nSilinen teklifler Ayarlar > Silinenler'den geri alınabilir.`)) return;
    try {
        // İşlem logunu sunucu yazar
        const res = await fetch(`${API_URL}/offers/bulk-delete`, {
            method: 'POST',
            headers: jsonAuthHeaders(currentUser?.token),
            body: JSON.stringify({ ids: selectedIds })
        });
        await readJsonOrThrow(res, 'Teklifler silinemedi.');
        incrementWrites(count);

        setSelectedIds([]);
        refreshData('offers');
        loadPdfIds();
        setNotice(`${count} teklif silindi. Ayarlar > Silinenler'den geri alınabilir.`);
    } catch (error) {
        console.error("Toplu silme hatası:", error);
        alert(`Teklifler silinemedi: ${error.message}`);
    }
  };

  const handleDuplicate = async (offer) => {
    if (duplicatingRef.current) return; // çift tıklama iki kopya açmasın
    if (!window.confirm(`${offer.customerName} için oluşturulan teklifi kopyalamak istiyor musunuz?`)) return;
    duplicatingRef.current = true;
    try {
      // Yalnızca beyaz listedeki alanlar kopyalanır (bkz. DUPLICATE_FIELDS)
      const copied = {};
      DUPLICATE_FIELDS.forEach((key) => {
        if (offer[key] !== undefined) copied[key] = offer[key];
      });
      if (DISCOUNT_FIELDS.some((key) => offer[key] !== null && offer[key] !== undefined)) {
        DISCOUNT_FIELDS.forEach((key) => { copied[key] = offer[key] ?? null; });
      }
      const userInfo = { uid: currentUser?.uid, email: currentUser?.email };

      const newOffer = {
        ...copied,
        customerName: `${offer.customerName || ''} (Kopya)`.trim(),
        checkIn: dateKey(offer.checkIn) || null,
        checkOut: dateKey(offer.checkOut) || null,
        status: 'Onay Bekliyor',
        // Kopya bugün oluşturulan YENİ bir tekliftir → aktif sezona yazılır (kaynağın sezonu taşınmaz)
        ...(isValidSeason(activeSeason) ? { season: activeSeason } : {}),
        createdAt: new Date(),
        createdBy: userInfo,
      };

      const res = await fetch(`${API_URL}/offers`, {
          method: 'POST',
          headers: jsonAuthHeaders(currentUser?.token),
          body: JSON.stringify(newOffer)
      });
      const created = await readJsonOrThrow(res, 'Teklif kopyalanamadı.');
      const newId = created?.id;

      incrementWrites(2); // Kayıt + Log
      // İşlem logu bilgi amaçlı: hatası kopyayı "başarısız" göstermesin
      fetch(`${API_URL}/activityLogs`, {
        method: 'POST',
        headers: jsonAuthHeaders(currentUser?.token),
        body: JSON.stringify({
          action: 'Teklif Oluşturuldu',
          description: `${offer.customerName} (#${offer.id}) teklifi kopyalandı${newId ? ` → #${newId}` : ''}.`,
          performedBy: userInfo
        })
      }).catch(() => {});

      // Kopya aktif sezona yazıldı: başka sezon görüntüleniyorsa listede görünmez → görünümü oraya al
      const copySeason = isValidSeason(activeSeason) ? activeSeason : offerSeason(offer);
      if (isValidSeason(copySeason) && selectedSeason !== ALL_SEASONS && selectedSeason !== copySeason) {
        setSelectedSeason(copySeason);
      }
      refreshData('offers');
      loadPdfIds();

      if (newId !== undefined && newId !== null) {
        // Kopyanın henüz PDF'i yok: düzenleme ekranında kaydedilince oluşur
        alert("Kopya oluşturuldu; düzenleyip kaydedince PDF'i hazırlanır ve gönderebilirsiniz.");
        navigate(`/offers/edit/${newId}`);
      } else {
        setNotice("Kopya oluşturuldu ancak numarası alınamadı. Listeden açıp kaydedince PDF'i hazırlanır.");
      }
    } catch (error) {
      console.error("Kopyalama hatası:", error);
      alert(`Teklif kopyalanamadı: ${error.message}`);
    } finally {
      duplicatingRef.current = false;
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    // Kanban'dan id metin olarak gelir → karşılaştırma String ile
    const offer = findOffer(id);
    if (offer && offer.status === newStatus) return; // aynı sütuna bırakıldı: değişiklik yok
    const customerName = offer ? offer.customerName : 'Bilinmeyen Müşteri';
    try {
      const res = await fetch(`${API_URL}/offers/${id}`, {
          method: 'PUT',
          headers: jsonAuthHeaders(currentUser?.token),
          body: JSON.stringify({ status: newStatus })
      });
      await readJsonOrThrow(res, 'Durum güncellenemedi.');

      incrementWrites(2); // Güncelleme + Log
      // Log hatası durum değişikliğini geri almaz → kullanıcıya hata gösterilmez
      fetch(`${API_URL}/activityLogs`, {
        method: 'POST',
        headers: jsonAuthHeaders(currentUser?.token),
        body: JSON.stringify({
          action: 'Teklif Durumu Güncellendi',
          description: `${customerName} (#${id}) teklif durumu "${newStatus}" olarak güncellendi.`,
          performedBy: { uid: currentUser?.uid, email: currentUser?.email }
        })
      }).catch(() => {});
      refreshData('offers');
      loadPdfIds();
    } catch (error) {
      console.error("Durum güncelleme hatası:", error);
      alert(`Durum güncellenemedi: ${error.message}`);
    }
  };

  // --- Paylaşım: mesaj metinleri Ayarlar > Mesaj Şablonları'ndan (utils/offerShare), link HER ZAMAN PDF linki ---
  const copyThenOpenInstagram = (message, username, copiedText) => {
    const open = () => window.open(instagramUrl(username), '_blank');
    if (!navigator.clipboard?.writeText) {
      open();
      return;
    }
    navigator.clipboard.writeText(message).then(() => {
        alert(copiedText);
        open();
    }).catch(err => {
        console.error('Metin kopyalanamadı:', err);
        open();
    });
  };

  // Teklif linki paylaşımları: önce iletişim bilgisi, sonra PDF kontrolü (PDF yoksa sorulur; vazgeçilirse paylaşım ve log yok)
  const handleWhatsAppShare = (offer) => {
    if (!waPhone(offer.phone)) {
      alert("Bu teklifte telefon numarası kayıtlı değil.");
      return;
    }
    if (!confirmPdfBeforeShare(offer, pdfIds)) return;
    const message = buildOfferMessage('waDetailed', offer, shareCtx);
    window.open(whatsappUrl(offer.phone, message), '_blank');
    logOfferShare(offer, 'WhatsApp (detaylı)', currentUser?.token);
  };

  const handleInstagramShare = (offer) => {
    if (!String(offer.instagram || '').replace(/[@\s]/g, '')) {
      alert("Bu teklifte Instagram kullanıcı adı kayıtlı değil.");
      return;
    }
    if (!confirmPdfBeforeShare(offer, pdfIds)) return;
    const message = buildOfferMessage('instagram', offer, shareCtx);
    logOfferShare(offer, 'Instagram', currentUser?.token);
    copyThenOpenInstagram(message, offer.instagram, "Mesaj metni kopyalandı! Instagram DM penceresi açıldığında yapıştırabilirsiniz.");
  };

  const handleWhatsAppLink = (offer) => {
    if (!waPhone(offer.phone)) {
      alert("Bu teklifte telefon numarası kayıtlı değil.");
      return;
    }
    if (!confirmPdfBeforeShare(offer, pdfIds)) return;
    const message = buildOfferMessage('waLink', offer, shareCtx);
    window.open(whatsappUrl(offer.phone, message), '_blank');
    logOfferShare(offer, 'WhatsApp (link)', currentUser?.token);
  };

  const handleEmailShare = (offer) => {
    if (!String(offer.email || '').trim()) {
      alert("Bu teklifte e-posta adresi kayıtlı değil.");
      return;
    }
    if (!confirmPdfBeforeShare(offer, pdfIds)) return;
    const { subject, body } = buildOfferEmail(offer, shareCtx);
    logOfferShare(offer, 'E-posta', currentUser?.token);
    window.location.href = mailtoUrl(offer.email, subject, body);
  };

  const handleOpenPriceListModal = (offer) => {
    setPriceListModal({ isOpen: true, offer });
  };

  const handleSendPriceList = (platform) => {
    const offer = priceListModal.offer;
    if (!offer) return;

    // Fiyat listesi linki: Ayarlar > Mesaj Şablonları (priceListLink) → Marka (roomsLink) → varsayılan
    const message = buildOfferMessage('priceList', offer, shareCtx);

    if (platform === 'whatsapp') {
        if (!waPhone(offer.phone)) return alert("Telefon numarası kayıtlı değil.");
        window.open(whatsappUrl(offer.phone, message), '_blank');
        logOfferShare(offer, 'Fiyat listesi (WhatsApp)', currentUser?.token);
    } else if (platform === 'instagram') {
        if (!String(offer.instagram || '').replace(/[@\s]/g, '')) return alert("Instagram kullanıcı adı kayıtlı değil.");
        logOfferShare(offer, 'Fiyat listesi (Instagram)', currentUser?.token);
        copyThenOpenInstagram(message, offer.instagram, "Mesaj kopyalandı! Instagram açılıyor...");
    } else if (platform === 'email') {
        if (!String(offer.email || '').trim()) return alert("E-posta adresi kayıtlı değil.");
        const subject = "Ramada by Wyndham Ergan - Fiyat Listesi";
        logOfferShare(offer, 'Fiyat listesi (E-posta)', currentUser?.token);
        window.location.href = mailtoUrl(offer.email, subject, message);
    }
    setPriceListModal({ isOpen: false, offer: null });
  };

  const handleViewPDF = async (offer) => {
    try {
      // Teklifteki oda bilgilerini güncel oda özellikleri ile birleştir
      const currentRoom = rooms.find(r => r.id === offer.room?.id);
      const offerWithFeatures = {
        ...offer,
        room: {
            ...offer.room,
            features: currentRoom?.features || offer.room?.features
        }
      };

      const blob = await pdf(<OfferPDF offer={offerWithFeatures} settings={settings} />).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error("PDF oluşturma hatası:", error);
      alert("PDF oluşturulurken bir hata oluştu.");
    }
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  // Detay penceresi: teklifteki odayı güncel oda özellikleriyle birleştirir
  const openDetail = (offer) => {
    const currentRoom = rooms.find(r => r.id === offer.room?.id);
    setSelectedOffer({
        ...offer,
        room: { ...offer.room, features: currentRoom?.features || offer.room?.features }
    });
  };

  // Müşteri geçmişinden "Listede göster": geçmişi eşleştiren anahtarla (telefon → e-posta → Instagram → ad) arar.
  // Geçmiş TÜM sezonlardan ve süzgeçsiz hesaplanır → sayılar tutsun diye sezon "Tüm Sezonlar"a alınır,
  // oluşturan ve tarih süzgeçleri temizlenir.
  const showCustomerInList = (offer) => {
    const key = customerSearchKey(offer);
    setSelectedOffer(null);
    setSearchTerm(key);
    setCreatorFilter('');
    setStartDate('');
    setEndDate('');
    setCreatedAtStart('');
    setCreatedAtEnd('');
    if (selectedSeason !== ALL_SEASONS) setSelectedSeason(ALL_SEASONS);
    updateParams({ q: key || null, status: null, filter: null, creator: null });
  };

  const filteredOffers = offers.filter(offer => {
    // Ad, telefon, e-posta veya Instagram (Türkçe İ/ı katlamalı; telefonda 0/+90 farkı önemsiz)
    const matchesText = matchesSearch([offer.customerName, offer.phone, offer.email, offer.instagram], searchTerm);
    const matchesStatus = statusFilter ? offer.status === statusFilter : true;
    const matchesCreator = creatorIds.has(String(offer.id));
    const matchesFollow = followFilter === 'takip' ? followIds.has(String(offer.id))
      : followFilter === 'gecmis' ? staleIds.has(String(offer.id))
      : true;

    // Tarihler API'den ISO zaman damgası gelir (bir gün kaymış olabilir) → İstanbul gününe çevirip karşılaştır
    let matchesDate = true;
    if (startDate || endDate) {
        const offerDate = dateKey(offer.checkIn);
        if (!offerDate) matchesDate = false;
        else {
            if (startDate && offerDate < startDate) matchesDate = false;
            if (endDate && offerDate > endDate) matchesDate = false;
        }
    }

    let matchesCreatedAt = true;
    if (createdAtStart || createdAtEnd) {
        const dateStr = dateKey(offer.createdAt);
        if (dateStr) {
            if (createdAtStart && dateStr < createdAtStart) matchesCreatedAt = false;
            if (createdAtEnd && dateStr > createdAtEnd) matchesCreatedAt = false;
        } else matchesCreatedAt = false; // Tarih yoksa ve filtre varsa gösterme
    }

    return matchesText && matchesStatus && matchesFollow && matchesDate && matchesCreator && matchesCreatedAt;
  }).sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  // --- Sayfalama (istemci tarafı) ---
  const totalPages = Math.max(1, Math.ceil(filteredOffers.length / PAGE_SIZE));
  // Filtre değişince liste kısalabilir; mevcut sayfa aralık dışında kalırsa son sayfaya çek
  const safePage = Math.min(currentPage, totalPages);
  const pageStartIndex = (safePage - 1) * PAGE_SIZE;
  const paginatedOffers = filteredOffers.slice(pageStartIndex, pageStartIndex + PAGE_SIZE);

  const goToPage = (page) => {
    const target = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(target);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Sayfa numarası penceresi: 1 … 4 5 [6] 7 8 … 27  ('...' = boşluk göstergesi)
  const pageNumbers = (() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, safePage, safePage - 1, safePage + 1]);
    if (safePage <= 3) [2, 3, 4].forEach(p => pages.add(p));
    if (safePage >= totalPages - 2) [totalPages - 1, totalPages - 2, totalPages - 3].forEach(p => pages.add(p));
    const sorted = [...pages].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    return sorted.reduce((acc, p, i) => {
      if (i > 0 && p - sorted[i - 1] > 1) acc.push('...');
      acc.push(p);
      return acc;
    }, []);
  })();

  // Filtre/arama/sıralama/sezon değiştiğinde ilk sayfaya dön
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, creatorFilter, statusFilter, followFilter, startDate, endDate, createdAtStart, createdAtEnd, sortOrder, selectedSeason]);

  // "Tümünü seç" yalnızca görüntülenen sayfayı kapsar (toplu silme kazası olmasın diye)
  const handleSelectAll = (e) => {
    const pageIds = paginatedOffers.map(o => o.id);
    if (e.target.checked) {
        setSelectedIds(prev => [...new Set([...prev, ...pageIds])]);
    } else {
        setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => 
        prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleDragStart = (e, id) => {
    e.dataTransfer.setData('offerId', id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, status) => {
    e.preventDefault();
    if (!canWrite) return;
    const id = e.dataTransfer.getData('offerId');
    if (id) {
        handleStatusChange(id, status);
    }
  };

  // Müşteri geçmişi (detay penceresi): aynı telefon/e-posta/Instagram ile verilmiş diğer teklifler — tüm sezonlar
  const customerHistory = selectedOffer ? customerOffers(allOffers, selectedOffer, { excludeId: selectedOffer.id }) : [];
  const customerHistoryText = customerSummaryText(customerSummary(customerHistory));

  const deleteTarget = deleteModal.isOpen ? findOffer(deleteModal.offerId) : null;

  const followCounts = { takip: followIds.size, gecmis: staleIds.size };

  // Teklifler hiç okunamadıysa liste boş görünür: "Henüz teklif oluşturulmamış" yazmak yanıltıcı olur
  const offersLoadFailed = !!offersError && allOffers.length === 0;

  const emptyText = offersLoadFailed
    ? 'Teklifler yüklenemedi. Üstteki "Tekrar dene" ile yeniden deneyin.'
    : offers.length === 0
      ? (selectedSeason !== ALL_SEASONS ? `${selectedSeason} sezonunda teklif yok. Üstteki seçiciden başka bir sezon seçebilirsiniz.` : "Henüz teklif oluşturulmamış.")
      : followFilter === 'takip' && followCounts.takip === 0
        ? "Takip edilecek teklif yok."
        : followFilter === 'gecmis' && followCounts.gecmis === 0
          ? "Giriş tarihi geçmiş bekleyen teklif yok."
          : "Aranan kriterlere uygun teklif bulunamadı.";

  if (loading.offers && !data.offers) return <LoadingSpinner />;

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Teklifler</h1>
        <div className="flex flex-wrap gap-2">
            <button 
                onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors mr-2 flex items-center justify-center"
                title={sortOrder === 'desc' ? 'En Yeniden En Eskiye' : 'En Eskiden En Yeniye'}
            >
                <ArrowUpDown size={20} />
            </button>
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1 mr-2">
                <button 
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-gray-600 shadow text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                >
                    <List size={20} />
                </button>
                <button 
                    onClick={() => setViewMode('kanban')}
                    className={`p-2 rounded-md transition-colors ${viewMode === 'kanban' ? 'bg-white dark:bg-gray-600 shadow text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                >
                    <LayoutGrid size={20} />
                </button>
            </div>
            {selectedIds.length > 0 && canDelete && (
                <button
                    onClick={handleBulkDelete}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-red-700 transition-colors animate-in fade-in"
                >
                    <Trash2 size={20} />
                    Seçilenleri Sil ({selectedIds.length})
                </button>
            )}
            {canWrite && (
              <Link
              to="/offers/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
              >
              <Plus size={20} />
              Yeni Teklif Oluştur
              </Link>
            )}
        </div>
      </div>

      {/* Teklifler okunamadı: eski liste (varsa) güncel olmayabilir */}
      {offersError && (
        <div role="alert" className="mb-6 p-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <AlertTriangle size={20} className="shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
            <div className="min-w-0 break-words text-sm">
              <p className="font-semibold">
                {allOffers.length > 0 ? 'Teklif listesi yenilenemedi.' : 'Teklifler yüklenemedi.'}
              </p>
              <p className="text-red-700 dark:text-red-300">
                {offersError}
                {allOffers.length > 0 && ' Gösterilen liste güncel olmayabilir.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={retryOffers}
            disabled={!!loading.offers}
            className="shrink-0 self-start sm:self-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 disabled:cursor-wait transition-colors"
          >
            <RefreshCw size={16} className={loading.offers ? 'animate-spin' : ''} />
            {loading.offers ? 'Yükleniyor…' : 'Tekrar dene'}
          </button>
        </div>
      )}

      {/* Durum Özet Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div
            onClick={() => selectStatus('')}
            className={`bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border ${statusFilter === '' && !followFilter ? 'border-blue-500 ring-1 ring-blue-500 dark:border-blue-400 dark:ring-blue-400' : 'border-gray-100 dark:border-gray-700'} flex items-center justify-between cursor-pointer hover:shadow-md transition-all`}
        >
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1 font-medium">Toplam Teklif</p>
            <h3 className="text-2xl font-bold text-blue-600 dark:text-blue-400">{offers.length}</h3>
          </div>
          <div className="p-3 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 bg-opacity-20">
            <FileText size={24} />
          </div>
        </div>

        <div 
            onClick={() => selectStatus('Onay Bekliyor')}
            className={`bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border ${statusFilter === 'Onay Bekliyor' ? 'border-yellow-500 ring-1 ring-yellow-500 dark:border-yellow-400 dark:ring-yellow-400' : 'border-gray-100 dark:border-gray-700'} flex items-center justify-between cursor-pointer hover:shadow-md transition-all`}
        >
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1 font-medium">Bekleyen</p>
            <h3 className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{offers.filter(o => o.status === 'Onay Bekliyor').length}</h3>
          </div>
          <div className="p-3 rounded-full bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 bg-opacity-20">
            <Clock size={24} />
          </div>
        </div>

        <div 
            onClick={() => selectStatus('Onaylandı')}
            className={`bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border ${statusFilter === 'Onaylandı' ? 'border-green-500 ring-1 ring-green-500 dark:border-green-400 dark:ring-green-400' : 'border-gray-100 dark:border-gray-700'} flex items-center justify-between cursor-pointer hover:shadow-md transition-all`}
        >
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1 font-medium">Onaylanan</p>
            <h3 className="text-2xl font-bold text-green-600 dark:text-green-400">{offers.filter(o => o.status === 'Onaylandı').length}</h3>
          </div>
          <div className="p-3 rounded-full bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 bg-opacity-20">
            <CheckCircle size={24} />
          </div>
        </div>

        <div 
            onClick={() => selectStatus('Onaylanmadı')}
            className={`bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border ${statusFilter === 'Onaylanmadı' ? 'border-red-500 ring-1 ring-red-500 dark:border-red-400 dark:ring-red-400' : 'border-gray-100 dark:border-gray-700'} flex items-center justify-between cursor-pointer hover:shadow-md transition-all`}
        >
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1 font-medium">Reddedilen</p>
            <h3 className="text-2xl font-bold text-red-600 dark:text-red-400">{offers.filter(o => o.status === 'Onaylanmadı').length}</h3>
          </div>
          <div className="p-3 rounded-full bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 bg-opacity-20">
            <XCircle size={24} />
          </div>
        </div>
      </div>

      {/* Filtreleme Alanı */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col md:flex-row gap-4 mb-4 md:items-end">
            <div className="relative w-full md:flex-1 min-w-0">
                <Search className="absolute left-3 top-3 text-gray-400 dark:text-gray-500" size={20} />
                <input
                    type="search"
                    placeholder="Ad, telefon, e-posta veya Instagram ile ara…"
                    className="w-full pl-10 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                />
            </div>
            <div className="relative w-full md:flex-1 min-w-0">
                <Search className="absolute left-3 top-3 text-gray-400 dark:text-gray-500" size={20} />
                <input
                    type="text"
                    placeholder="Oluşturan ile ara..."
                    className="w-full pl-10 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                    value={creatorFilter}
                    onChange={(e) => handleCreatorChange(e.target.value)}
                />
            </div>
            <div className="w-full md:w-auto">
                <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1 ml-1">Giriş Tarihi</span>
                <div className="flex gap-2">
                    <input
                        type="date"
                        aria-label="Giriş tarihi başlangıç"
                        className="flex-1 md:flex-none min-w-0 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                    <input
                        type="date"
                        aria-label="Giriş tarihi bitiş"
                        className="flex-1 md:flex-none min-w-0 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </div>
            </div>
            <div className="w-full md:w-auto">
                <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1 ml-1">Oluşturulma Tarihi</span>
                <div className="flex gap-2">
                    <input
                        type="date"
                        aria-label="Oluşturulma tarihi başlangıç"
                        className="flex-1 md:flex-none min-w-0 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={createdAtStart}
                        onChange={(e) => setCreatedAtStart(e.target.value)}
                    />
                    <input
                        type="date"
                        aria-label="Oluşturulma tarihi bitiş"
                        className="flex-1 md:flex-none min-w-0 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={createdAtEnd}
                        onChange={(e) => setCreatedAtEnd(e.target.value)}
                    />
                </div>
            </div>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {['', ...STATUSES].map((status) => (
            <button
              key={status || 'all'}
              onClick={() => selectStatus(status)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                statusFilter === status && !followFilter
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {status === '' ? 'Tümü' : status}
            </button>
          ))}
          <span className="self-stretch w-px bg-gray-200 dark:bg-gray-600 mx-1 shrink-0" aria-hidden="true" />
          {Object.entries(FOLLOW_FILTERS).map(([key, label]) => {
            const active = followFilter === key;
            const count = followCounts[key];
            const Icon = key === 'takip' ? PhoneCall : CalendarX;
            return (
              <button
                key={key}
                onClick={() => selectFollowFilter(active ? '' : key)}
                aria-pressed={active}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                  active
                    ? 'bg-blue-600 text-white'
                    : count > 0
                      ? (key === 'takip'
                        ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30'
                        : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30')
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <Icon size={14} />
                {label} ({count})
              </button>
            );
          })}
        </div>
        {followFilter && (
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
            <span>
              {followFilter === 'takip'
                ? `Onay bekleyen ve ${FOLLOW_UP.warnDays}+ gündür cevapsız (${FOLLOW_UP.lateDays}+ gün kırmızı) ya da girişine ${FOLLOW_UP.checkInSoonDays} gün veya daha az kalan teklifler.`
                : "Giriş tarihi geçtiği hâlde hâlâ 'Onay Bekliyor' olan teklifler — durumlarını güncelleyin."}
            </span>
            <button onClick={() => selectFollowFilter('')} className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
              Süzgeci kaldır
            </button>
          </div>
        )}
      </div>

      {/* Kanban Görünümü */}
      {viewMode === 'kanban' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 overflow-x-auto pb-4">
            {STATUSES.map(status => (
                <div
                    key={status}
                    className={`bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border-2 border-dashed transition-colors ${
                        status === 'Onaylandı' ? 'border-green-200 dark:border-green-900/30' : 
                        status === 'Onaylanmadı' ? 'border-red-200 dark:border-red-900/30' : 
                        'border-yellow-200 dark:border-yellow-900/30'
                    }`}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, status)}
                >
                    <h3 className={`font-bold mb-4 flex items-center justify-between ${
                        status === 'Onaylandı' ? 'text-green-700 dark:text-green-400' : 
                        status === 'Onaylanmadı' ? 'text-red-700 dark:text-red-400' : 
                        'text-yellow-700 dark:text-yellow-400'
                    }`}>
                        {status}
                        <span className="bg-white dark:bg-gray-700 px-2 py-0.5 rounded-full text-xs shadow-sm">
                            {filteredOffers.filter(o => o.status === status).length}
                        </span>
                    </h3>
                    <div className="space-y-3">
                        {filteredOffers.filter(o => o.status === status).map(offer => {
                            return (
                                <div
                                    key={offer.id}
                                    draggable={!!canWrite}
                                    onDragStart={(e) => handleDragStart(e, offer.id)}
                                    className={`bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all ${canWrite ? 'cursor-move active:cursor-grabbing' : 'cursor-pointer'}`}
                                    onClick={() => openDetail(offer)}
                                    title="Detay için tıklayın"
                                >
                                    <div className="flex justify-between items-start gap-2 mb-2">
                                        <h4 className="font-semibold text-gray-800 dark:text-white text-sm break-words min-w-0">{offer.customerName}</h4>
                                        <div className="flex flex-wrap justify-end gap-1 shrink-0">
                                            <RepeatBadge count={repeatCountOf(offer)} />
                                            <NoPdfBadge show={pdfMissing(offer)} />
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{offerRoomsText(offer) || offer.room?.name}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                        {formatStay(offer) || '-'}
                                    </p>
                                    <FollowUpBadges f={followUpOf(offer)} className="mb-2" />
                                    <div className="flex justify-between items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                                        <span className="font-bold text-blue-600 dark:text-blue-400 text-sm whitespace-nowrap">
                                            {offerMoneyText(offer)}
                                        </span>
                                        <span className="text-[10px] text-gray-400 truncate">{offer.source?.name}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
      )}

      {/* Mobil Görünüm (Kartlar) */}
      <div className={`md:hidden space-y-4 ${viewMode === 'kanban' ? 'hidden' : ''}`}>
        {filteredOffers.length === 0 ? (
            <div className={`text-center p-4 bg-white dark:bg-gray-800 rounded-lg shadow break-words ${offersLoadFailed ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                {emptyText}
            </div>
        ) : (
            paginatedOffers.map((offer) => {
                return (
                <div key={offer.id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow duration-200">
                    <div
                        className="p-4 flex justify-between items-center gap-3 cursor-pointer"
                        onClick={() => toggleRow(offer.id)}
                    >
                        <div className="shrink-0" onClick={e => e.stopPropagation()}>
                            <input
                                type="checkbox"
                                checked={selectedIds.includes(offer.id)}
                                onChange={() => handleSelectOne(offer.id)}
                                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <h3 className="font-bold text-gray-800 dark:text-white break-words min-w-0">{offer.customerName}</h3>
                                <RepeatBadge count={repeatCountOf(offer)} />
                                <NoPdfBadge show={pdfMissing(offer)} />
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 break-words">{offerRoomsText(offer) || offer.room?.name}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusPillClass(offer.status)}`}>
                                    {offer.status}
                                </span>
                                <span className="text-sm font-bold text-blue-600 dark:text-blue-400 whitespace-nowrap">{offerMoneyText(offer)}</span>
                            </div>
                            <FollowUpBadges f={followUpOf(offer)} className="mt-1.5" />
                        </div>
                        {expandedRow === offer.id ? <ChevronUp size={20} className="text-gray-400 shrink-0" /> : <ChevronDown size={20} className="text-gray-400 shrink-0" />}
                    </div>

                    {expandedRow === offer.id && (
                        <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                            <div className="mt-3 space-y-2 text-sm text-gray-600 dark:text-gray-300 break-words">
                                <p><span className="font-medium">Tarih:</span> {formatStay(offer) || '-'}</p>
                                <p><span className="font-medium">Kaynak:</span> {offer.source?.name}</p>
                                <p><span className="font-medium">Oluşturan:</span> {offer.createdBy?.email || '-'}</p>
                                <p><span className="font-medium">Oluşturulma:</span> {offer.createdAt ? new Date(offer.createdAt).toLocaleDateString('tr-TR') : '-'} {offer.createdAt ? new Date(offer.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
                                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                    <button onClick={(e) => { e.stopPropagation(); openDetail(offer); }} className="flex items-center gap-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 px-3 py-1.5 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600">
                                        <Eye size={16} /> Detay
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleWhatsAppShare(offer); }} className="flex items-center gap-1 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-3 py-1.5 rounded text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30">
                                        <MessageCircle size={16} /> WP
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleWhatsAppLink(offer); }} className="flex items-center gap-1 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-3 py-1.5 rounded text-green-700 dark:text-green-500 hover:bg-green-100 dark:hover:bg-green-900/30">
                                        <Share size={16} /> Link
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleInstagramShare(offer); }} className="flex items-center gap-1 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 px-3 py-1.5 rounded text-pink-600 dark:text-pink-400 hover:bg-pink-100 dark:hover:bg-pink-900/30">
                                        <Instagram size={16} /> IG
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleEmailShare(offer); }} className="flex items-center gap-1 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 px-3 py-1.5 rounded text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30">
                                        <Mail size={16} /> Email
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleOpenPriceListModal(offer); }} className="flex items-center gap-1 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 px-3 py-1.5 rounded text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/30">
                                        <BookOpen size={16} /> Fiyat
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleViewPDF(offer); }} className="flex items-center gap-1 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-1.5 rounded text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30">
                                        <FileText size={16} /> PDF
                                    </button>
                                    {canWrite && (
                                    <Link to={`/offers/edit/${offer.id}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 px-3 py-1.5 rounded text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-600">
                                        <Pencil size={16} /> Düzenle
                                    </Link>
                                    )}
                                    {canWrite && (
                                    <button onClick={(e) => { e.stopPropagation(); handleDuplicate(offer); }} className="flex items-center gap-1 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 px-3 py-1.5 rounded text-orange-700 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/30">
                                        <Copy size={16} /> Kopyala
                                    </button>
                                    )}
                                    {canDelete && (
                                    <button onClick={(e) => { e.stopPropagation(); handleDelete(offer.id); }} className="flex items-center gap-1 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-1.5 rounded text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30">
                                        <Trash2 size={16} /> Sil
                                    </button>
                                    )}
                                </div>
                                {canWrite && (
                                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                    <select
                                        value={offer.status}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => handleStatusChange(offer.id, e.target.value)}
                                        aria-label="Teklif durumu"
                                        className="w-full p-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    >
                                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )})
        )}
      </div>

      {/* Masaüstü Görünüm (Tablo) */}
      <div className={`hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden overflow-x-auto ${viewMode === 'kanban' ? 'hidden' : ''}`}>
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left">
                <input 
                    type="checkbox" 
                    onChange={handleSelectAll}
                    checked={paginatedOffers.length > 0 && paginatedOffers.every(o => selectedIds.includes(o.id))}
                    title="Bu sayfadaki teklifleri seç"
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Müşteri</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Oda & Tarih</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tutar</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Oluşturan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Oluşturulma</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Durum</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">İşlemler</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredOffers.length === 0 ? (
              <tr>
                <td colSpan="8" className={`px-6 py-4 text-center ${offersLoadFailed ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {emptyText}
                </td>
              </tr>
            ) : (
              paginatedOffers.map((offer) => {
                return (
                <tr
                  key={offer.id}
                  className={`transition-colors ${
                    offer.status === 'Onaylandı' ? 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30' : 
                    offer.status === 'Onaylanmadı' ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30' : 
                    offer.status === 'Onay Bekliyor' ? 'bg-yellow-50 dark:bg-yellow-900/20 hover:bg-yellow-100 dark:hover:bg-yellow-900/30' : 
                    'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input 
                        type="checkbox" 
                        checked={selectedIds.includes(offer.id)}
                        onChange={() => handleSelectOne(offer.id)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        {offer.customerName}
                        <RepeatBadge count={repeatCountOf(offer)} />
                        <NoPdfBadge show={pdfMissing(offer)} />
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{offer.source?.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white max-w-[240px] truncate" title={offerRoomsText(offer) || offer.room?.name}>{offerRoomsText(offer) || offer.room?.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {formatStay(offer) || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 dark:text-white">
                    {offerMoneyText(offer)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {offer.createdBy?.email || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div>{offer.createdAt ? new Date(offer.createdAt).toLocaleDateString('tr-TR') : '-'}</div>
                    <div className="text-xs text-gray-400">{offer.createdAt ? new Date(offer.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : ''}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {canWrite ? (
                    <select
                      value={offer.status}
                      onChange={(e) => handleStatusChange(offer.id, e.target.value)}
                      className={`text-xs font-semibold rounded-full px-2 py-1 border-0 cursor-pointer focus:ring-2 focus:ring-offset-1 outline-none ${
                        offer.status === 'Onaylandı' ? 'bg-green-100 text-green-800 focus:ring-green-500' : 
                        offer.status === 'Onaylanmadı' ? 'bg-red-100 text-red-800 focus:ring-red-500' : 
                        'bg-yellow-100 text-yellow-800 focus:ring-yellow-500'
                      }`}
                    >
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    ) : (
                      <span className={`text-xs font-semibold rounded-full px-2 py-1 ${statusPillClass(offer.status)}`}>{offer.status}</span>
                    )}
                    <FollowUpBadges f={followUpOf(offer)} className="mt-1.5" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button onClick={() => openDetail(offer)} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mr-2 inline-block" title="Detaylar">
                      <Eye size={18} />
                    </button>
                    <button 
                      onClick={() => handleWhatsAppShare(offer)} 
                      className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 mr-2 inline-block" 
                      title="WhatsApp ile Gönder"
                    >
                      <MessageCircle size={18} />
                    </button>
                    <button 
                      onClick={() => handleInstagramShare(offer)} 
                      className="text-pink-600 dark:text-pink-400 hover:text-pink-800 dark:hover:text-pink-300 mr-2 inline-block" 
                      title="Instagram DM Gönder"
                    >
                      <Instagram size={18} />
                    </button>
                    <button 
                      onClick={() => handleWhatsAppLink(offer)} 
                      className="text-green-700 dark:text-green-500 hover:text-green-900 dark:hover:text-green-400 mr-2 inline-block" 
                      title="WhatsApp ile PDF Linki Gönder"
                    >
                      <Share size={18} />
                    </button>
                    <button 
                      onClick={() => handleEmailShare(offer)} 
                      className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 mr-2 inline-block" 
                      title="E-posta Gönder"
                    >
                      <Mail size={18} />
                    </button>
                    <button 
                      onClick={() => handleOpenPriceListModal(offer)} 
                      className="text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 mr-2 inline-block" 
                      title="Fiyat Listesi Gönder"
                    >
                      <BookOpen size={18} />
                    </button>
                    {canWrite && (
                    <Link to={`/offers/edit/${offer.id}`} className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-2 inline-block" title="Düzenle">
                      <Pencil size={18} />
                    </Link>
                    )}
                    {canWrite && (
                    <button onClick={() => handleDuplicate(offer)} className="text-orange-600 hover:text-orange-900 mr-2 inline-block" title="Kopyala">
                      <Copy size={18} />
                    </button>
                    )}
                    <button
                      onClick={() => handleViewPDF(offer)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-4 inline-block"
                      title="PDF Görüntüle"
                    >
                      <FileText size={18} />
                    </button>
                    {canDelete && (
                    <button onClick={() => handleDelete(offer.id)} className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300" title="Sil">
                      <Trash2 size={18} />
                    </button>
                    )}
                  </td>
                </tr>
              )})
            )}
          </tbody>
        </table>
      </div>

      {/* Sayfalama (Kanban görünümünde gizli) */}
      {viewMode !== 'kanban' && filteredOffers.length > 0 && (
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white dark:bg-gray-800 rounded-lg shadow px-4 py-3 print:hidden">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium text-gray-800 dark:text-gray-200">{filteredOffers.length}</span> teklif içinden{' '}
            <span className="font-medium text-gray-800 dark:text-gray-200">
              {pageStartIndex + 1}-{Math.min(pageStartIndex + PAGE_SIZE, filteredOffers.length)}
            </span>{' '}
            arası gösteriliyor
          </p>

          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => goToPage(safePage - 1)}
                disabled={safePage === 1}
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Önceki
              </button>

              {pageNumbers.map((page, i) =>
                page === '...' ? (
                  <span key={`gap-${i}`} className="px-2 text-gray-400 select-none">…</span>
                ) : (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    aria-current={page === safePage ? 'page' : undefined}
                    className={`min-w-[36px] px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      page === safePage
                        ? 'bg-blue-600 text-white shadow'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {page}
                  </button>
                )
              )}

              <button
                onClick={() => goToPage(safePage + 1)}
                disabled={safePage === totalPages}
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Sonraki
              </button>
            </div>
          )}
        </div>
      )}

      {/* Teklif Detay Modalı */}
      {selectedOffer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                Teklif Detayları <span className="text-sm font-normal text-gray-400">#{selectedOffer.id}</span>
              </h3>
              <button onClick={() => setSelectedOffer(null)} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" aria-label="Kapat">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="min-w-0 break-words">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Müşteri Bilgileri</h4>
                <p className="text-gray-900 dark:text-white font-medium flex flex-wrap items-center gap-2">
                  {selectedOffer.customerName}
                  <RepeatBadge count={repeatCountOf(selectedOffer)} />
                  <NoPdfBadge show={pdfMissing(selectedOffer)} />
                </p>
                {selectedOffer.phone && <p className="text-gray-600 dark:text-gray-300 text-sm">{selectedOffer.phone}</p>}
                {selectedOffer.email && <p className="text-gray-600 dark:text-gray-300 text-sm">{selectedOffer.email}</p>}
                {selectedOffer.instagram && <p className="text-gray-600 dark:text-gray-300 text-sm">IG: {selectedOffer.instagram}</p>}
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Konaklama Detayları</h4>
                {selectedOffer.rooms && selectedOffer.rooms.length > 0 ? (
                    <div className="space-y-2 border dark:border-gray-600 rounded p-2 bg-gray-50 dark:bg-gray-700 max-h-48 overflow-y-auto">
                        {selectedOffer.rooms.map((room, idx) => (
                            <div key={idx} className="border-b border-gray-200 dark:border-gray-600 pb-2 last:border-0 last:pb-0">
                                <p className="text-gray-900 dark:text-white text-sm font-medium">{room.name}</p>
                                <div className="flex justify-between items-center mt-1">
                                    <p className="text-gray-500 dark:text-gray-400 text-xs">
                                        {formatDateRangeTR(room.checkIn || selectedOffer.checkIn, room.checkOut || selectedOffer.checkOut) || '-'}
                                    </p>
                                    <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-1.5 py-0.5 rounded whitespace-nowrap">{room.nights} Gece</span>
                                </div>
                                <p className="text-right text-xs font-bold text-gray-700 dark:text-gray-300 mt-1">
                                    {formatMoneyTR(roomLineTotal(room))} {selectedOffer.currency || 'TL'}
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        <p className="text-gray-900 dark:text-white">{selectedOffer.room?.name}</p>
                        <p className="text-gray-600 dark:text-gray-300 text-sm">{formatStay(selectedOffer) || '-'}</p>
                    </>
                )}
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Tutar ve Durum</h4>
                {(() => {
                  const d = offerDiscount(selectedOffer);
                  if (d.amount <= 0) return null;
                  const fmt = (n) => `${formatMoneyTR(n)} ${selectedOffer.currency || 'TL'}`;
                  return (
                    <div className="text-xs text-gray-600 dark:text-gray-300 mb-1 space-y-0.5">
                      <p>Ara Toplam: {fmt(d.subTotal)}</p>
                      <p className="text-green-700 dark:text-green-400">{discountLabel(d)}: -{fmt(d.amount)}</p>
                    </div>
                  );
                })()}
                <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{offerMoneyText(selectedOffer)}</p>
                <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-semibold ${statusPillClass(selectedOffer.status)}`}>
                  {selectedOffer.status}
                </span>
                <FollowUpBadges f={followUpOf(selectedOffer)} className="mt-2" />
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Diğer Bilgiler</h4>
                {offerSeason(selectedOffer) && (
                  <p className="text-gray-600 dark:text-gray-300 text-sm">Sezon: {offerSeason(selectedOffer)}</p>
                )}
                <p className="text-gray-600 dark:text-gray-300 text-sm">Kaynak: {selectedOffer.source?.name}</p>
                <p className="text-gray-600 dark:text-gray-300 text-sm">Oluşturan: {selectedOffer.createdBy?.email || '-'}</p>
                <p className="text-gray-600 dark:text-gray-300 text-sm">Tarih: {selectedOffer.createdAt ? new Date(selectedOffer.createdAt).toLocaleDateString('tr-TR') : '-'}</p>
              </div>

              {/* Müşteri geçmişi: aynı telefon / e-posta / Instagram ile verilmiş diğer teklifler (tüm sezonlar) */}
              {customerHistory.length > 0 && (
                <div className="md:col-span-2 border-t dark:border-gray-700 pt-4 min-w-0">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-2">
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                            <History size={16} /> Müşteri geçmişi
                        </h4>
                        <span className="text-xs text-gray-600 dark:text-gray-300">Diğer {customerHistoryText}</span>
                    </div>
                    <ul className="border dark:border-gray-600 rounded divide-y divide-gray-100 dark:divide-gray-700 overflow-hidden">
                        {customerHistory.slice(0, HISTORY_LIMIT).map((o) => (
                            <li key={o.id}>
                                <button
                                    type="button"
                                    onClick={() => openDetail(o)}
                                    title="Bu teklifin detayını aç"
                                    className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3"
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-gray-900 dark:text-white truncate">
                                            {offerRoomsText(o) || '-'}
                                            <span className="text-xs text-gray-400 ml-1">#{o.id}</span>
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {formatStay(o) || '-'} · Teklif: {formatDateTR(o.createdAt) || '-'}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${statusPillClass(o.status)}`}>{o.status}</span>
                                        {canViewFinancials && (
                                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap">{offerMoneyText(o)}</span>
                                        )}
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>
                    {customerHistory.length > HISTORY_LIMIT && (
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <span>ve {customerHistory.length - HISTORY_LIMIT} teklif daha</span>
                            <button type="button" onClick={() => showCustomerInList(selectedOffer)} className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
                                Listede göster
                            </button>
                        </div>
                    )}
                </div>
              )}

              {selectedOffer.room?.features && (
                <div className="md:col-span-2 border-t dark:border-gray-700 pt-4">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Oda Özellikleri</h4>
                    <div className="text-gray-600 dark:text-gray-300 text-sm prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: selectedOffer.room.features }} />
                </div>
              )}
            </div>

            <div className="p-6 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex justify-between items-center gap-3">
              <div className="flex gap-2 flex-wrap">
                <button
                    onClick={() => handleWhatsAppShare(selectedOffer)}
                    title="WhatsApp ile Gönder"
                    className="flex items-center gap-2 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 px-3 py-2 rounded-lg transition-colors"
                >
                    <MessageCircle size={20} />
                    <span className="text-sm font-medium hidden sm:inline">WhatsApp</span>
                </button>
                <button
                    onClick={() => handleWhatsAppLink(selectedOffer)}
                    title="WhatsApp ile PDF Linki Gönder"
                    className="flex items-center gap-2 text-green-700 dark:text-green-500 hover:bg-green-100 dark:hover:bg-green-900/30 px-3 py-2 rounded-lg transition-colors"
                >
                    <Share size={20} />
                    <span className="text-sm font-medium hidden sm:inline">WP Link</span>
                </button>
                <button
                    onClick={() => handleInstagramShare(selectedOffer)}
                    title="Instagram DM Gönder"
                    className="flex items-center gap-2 text-pink-600 dark:text-pink-400 hover:bg-pink-100 dark:hover:bg-pink-900/30 px-3 py-2 rounded-lg transition-colors"
                >
                    <Instagram size={20} />
                    <span className="text-sm font-medium hidden sm:inline">Instagram</span>
                </button>
                <button 
                    onClick={() => handleViewPDF(selectedOffer)} 
                    className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 px-3 py-2 rounded-lg transition-colors"
                >
                    <FileText size={20} />
                    <span className="text-sm font-medium hidden sm:inline">PDF</span>
                </button>
                <button 
                    onClick={() => handleEmailShare(selectedOffer)} 
                    className="flex items-center gap-2 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30 px-3 py-2 rounded-lg transition-colors"
                >
                    <Mail size={20} />
                    <span className="text-sm font-medium hidden sm:inline">E-posta</span>
                </button>
                <button 
                    onClick={() => handleOpenPriceListModal(selectedOffer)} 
                    className="flex items-center gap-2 text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/30 px-3 py-2 rounded-lg transition-colors"
                >
                    <BookOpen size={20} />
                    <span className="text-sm font-medium hidden sm:inline">Fiyat Listesi</span>
                </button>
                {canWrite && (
                <Link 
                    to={`/offers/edit/${selectedOffer.id}`}
                    className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 px-3 py-2 rounded-lg transition-colors"
                >
                    <Pencil size={20} />
                    <span className="text-sm font-medium hidden sm:inline">Düzenle</span>
                </Link>
                )}
              </div>
              <button 
                onClick={() => setSelectedOffer(null)}
                className="bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white px-4 py-2 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Silme Onay Modalı */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 transform transition-all scale-100">
                <div className="flex items-center gap-4 mb-4">
                    <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-full text-red-600 dark:text-red-400">
                        <AlertTriangle size={24} />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Teklifi Sil</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Teklif "Silinenler"e taşınır.</p>
                    </div>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-2 break-words">
                    {deleteTarget
                      ? <><strong>{deleteTarget.customerName}</strong> (#{deleteTarget.id}) teklifini silmek istediğinize emin misiniz?</>
                      : 'Bu teklifi silmek istediğinize emin misiniz?'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                    Silinen teklif Ayarlar &gt; Silinenler'den geri alınabilir.
                </p>
                <div className="flex justify-end gap-3">
                    <button
                        onClick={() => setDeleteModal({ isOpen: false, offerId: null })}
                        disabled={deleting}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
                    >
                        İptal
                    </button>
                    <button
                        onClick={executeDelete}
                        disabled={deleting}
                        className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-wait"
                    >
                        <Trash2 size={18} />
                        {deleting ? 'Siliniyor…' : 'Sil'}
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* Kısa bilgi (silme sonrası vb.) */}
      {notice && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:max-w-sm z-[60] bg-gray-900 dark:bg-gray-700 text-white text-sm px-4 py-3 rounded-lg shadow-lg flex items-start gap-3"
        >
          <CheckCircle size={18} className="text-green-400 shrink-0 mt-0.5" />
          <span className="flex-1 min-w-0 break-words">{notice}</span>
          <button onClick={() => setNotice('')} className="text-gray-300 hover:text-white shrink-0" aria-label="Kapat">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Fiyat Listesi Gönderme Modalı */}
      {priceListModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full p-6 transform transition-all scale-100">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Fiyat Listesi Gönder</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    <strong>{priceListModal.offer?.customerName}</strong> isimli müşteriye fiyat listesi linki gönderilecek.
                </p>
                <div className="space-y-3">
                    <button onClick={() => handleSendPriceList('whatsapp')} className="w-full flex items-center justify-center gap-2 bg-green-600 text-white p-3 rounded-lg hover:bg-green-700 transition-colors">
                        <MessageCircle size={20} /> WhatsApp ile Gönder
                    </button>
                    <button onClick={() => handleSendPriceList('instagram')} className="w-full flex items-center justify-center gap-2 bg-pink-600 text-white p-3 rounded-lg hover:bg-pink-700 transition-colors">
                        <Instagram size={20} /> Instagram ile Gönder
                    </button>
                    <button onClick={() => handleSendPriceList('email')} className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white p-3 rounded-lg hover:bg-purple-700 transition-colors">
                        <Mail size={20} /> E-posta ile Gönder
                    </button>
                    <button onClick={() => setPriceListModal({ isOpen: false, offer: null })} className="w-full p-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg mt-2 transition-colors">
                        İptal
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
}
